import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import useJobItem from 'cms/features/feature_job/hooks/UseJobItem';
import RecentJobslist from '../components/RecentJobslist';
import { Utils } from 'core/utils';
import {
    Box, Container, Grid, Typography, Button, Chip, Avatar, Stack,
    Divider, Paper, CircularProgress, Alert, Breadcrumbs, Link,
    IconButton, Tooltip
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f', '#558b2f', '#6d4c41'];
const nameToColor = (name = '') => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

function JobDetailsPage() {
    const { id } = useParams();
    const { data: job, isLoading, isError } = useJobItem(id);

    const pageShell = (children) => (
        <>
            <Header />
            <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pt: { xs: '60px', md: '68px' } }}>
                {children}
            </Box>
            <MinimalFooter />
        </>
    );

    if (isLoading) return pageShell(
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
        </Box>
    );

    if (isError || !job) return pageShell(
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Alert severity="error">Error loading job details. Please try again.</Alert>
        </Container>
    );

    const companyName = job.company?.title || 'Unknown Company';
    const logoUrl = job.company?.logo || null;
    const salary = job.pay_minimum && job.pay_maximum
        ? `$${job.pay_minimum} – $${job.pay_maximum} / ${job.pay_rate}`
        : null;

    const overviewItems = [
        { icon: <WorkOutlineIcon fontSize="small" />, label: 'Job Type', value: job.jobtypes?.map(jt => jt.title).join(', ') || 'N/A' },
        { icon: <LocationOnOutlinedIcon fontSize="small" />, label: 'Location', value: job.company?.city || job.address || 'N/A' },
        { icon: <AttachMoneyIcon fontSize="small" />, label: 'Salary', value: salary || 'Not specified' },
        { icon: <CalendarTodayIcon fontSize="small" />, label: 'Date Posted', value: Utils.timeAgo(job.created_at) },
        ...(job.expire_date ? [{ icon: <EventBusyOutlinedIcon fontSize="small" />, label: 'Expires', value: job.expire_date }] : []),
    ];

    const sidebarDetails = [
        { label: 'Job Type', value: job.jobtypes?.map(jt => jt.title).join(', ') || 'N/A' },
        { label: 'Location', value: job.company?.city || job.address || 'N/A' },
        { label: 'Salary', value: salary || 'Not specified' },
        ...(job.expire_date ? [{ label: 'Expires', value: job.expire_date }] : []),
    ];

    return pageShell(
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ mb: 2.5 }}
            >
                <Link component={RouterLink} to="/jobs" underline="hover" color="text.secondary" variant="body2">
                    Jobs
                </Link>
                <Link component={RouterLink} to="/employers" underline="hover" color="text.secondary" variant="body2">
                    {companyName}
                </Link>
                <Typography variant="body2" color="text.primary">{job.title}</Typography>
            </Breadcrumbs>

            <Grid container spacing={3}>
                {/* ── Main column ── */}
                <Grid item xs={12} md={8}>

                    {/* Hero card */}
                    <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Avatar
                                src={logoUrl}
                                variant="rounded"
                                sx={{
                                    width: 64, height: 64,
                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(companyName),
                                    fontSize: 26, fontWeight: 700,
                                    border: '1px solid', borderColor: 'divider',
                                    flexShrink: 0,
                                }}
                            >
                                {companyName.charAt(0).toUpperCase()}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                                    {job.title}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        {companyName}
                                    </Typography>
                                    {(job.address || job.company?.city) && (
                                        <>
                                            <Typography variant="body2" color="text.disabled">·</Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {job.address || job.company?.city}
                                                </Typography>
                                            </Stack>
                                        </>
                                    )}
                                    <Typography variant="body2" color="text.disabled">·</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {Utils.timeAgo(job.created_at)}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                    {job.jobtypes?.map(jt => (
                                        <Chip key={jt.id} label={jt.title} size="small" variant="outlined" />
                                    ))}
                                    {salary && (
                                        <Chip label={salary} size="small" color="success" variant="outlined" />
                                    )}
                                </Stack>
                            </Box>

                            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                <Tooltip title="Save job">
                                    <IconButton size="small"><BookmarkBorderIcon fontSize="small" /></IconButton>
                                </Tooltip>
                                <Tooltip title="Share">
                                    <IconButton size="small"><ShareOutlinedIcon fontSize="small" /></IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>

                        <Divider sx={{ my: 2.5 }} />

                        <Stack direction="row" spacing={1.5}>
                            <Button
                                component={RouterLink}
                                to={`/job/${id}/apply`}
                                variant="contained"
                                size="large"
                                disableElevation
                                sx={{ px: 4 }}
                            >
                                Apply Now
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<BookmarkBorderIcon />}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Overview grid */}
                    <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Job Overview
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            {overviewItems.map((item, idx) => (
                                <Grid item xs={6} sm={4} key={idx}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: 1.5,
                                            bgcolor: 'primary.50', color: 'primary.main',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {item.icon}
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>

                    {/* Description + Schedules + Benefits */}
                    <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Job Description
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line', lineHeight: 1.85 }}
                        >
                            {job.description || 'No job description available.'}
                        </Typography>

                        {job.schedules?.length > 0 && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Schedules
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                    {job.schedules.map(s => (
                                        <Chip key={s.id} label={s.title} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            </>
                        )}

                        {job.benefits?.length > 0 && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Benefits
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                    {job.benefits.map(b => (
                                        <Chip key={b.id} label={b.title} size="small" color="success" variant="outlined" />
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Paper>

                    {/* Bottom CTA banner */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5, mb: 3,
                            border: '1px solid', borderColor: 'primary.200',
                            borderRadius: 2,
                            bgcolor: 'primary.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600}>{job.title}</Typography>
                            <Typography variant="body2" color="text.secondary">{companyName}</Typography>
                        </Box>
                        <Button
                            component={RouterLink}
                            to={`/job/${id}/apply`}
                            variant="contained"
                            disableElevation
                            sx={{ flexShrink: 0 }}
                        >
                            Apply Now
                        </Button>
                    </Paper>

                    {/* Recent jobs */}
                    <RecentJobslist />
                </Grid>

                {/* ── Sticky sidebar ── */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            position: 'sticky',
                            top: { xs: 72, md: 84 },
                        }}
                    >
                        {/* Company block */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Avatar
                                src={logoUrl}
                                variant="rounded"
                                sx={{
                                    width: 48, height: 48,
                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(companyName),
                                    fontSize: 18, fontWeight: 700,
                                    border: '1px solid', borderColor: 'divider',
                                    flexShrink: 0,
                                }}
                            >
                                {companyName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    {companyName}
                                </Typography>
                                {job.company?.website && (
                                    <Link
                                        href={`https://${job.company.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="caption"
                                        color="primary"
                                        underline="hover"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                                    >
                                        {job.company.website}
                                        <OpenInNewIcon sx={{ fontSize: 10 }} />
                                    </Link>
                                )}
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={2} sx={{ mb: 2.5 }}>
                            {sidebarDetails.map(item => (
                                <Box key={item.label}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                                        {item.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}

                            {(job.contact_email || job.company?.phone_number) && (
                                <>
                                    <Divider />
                                    {job.contact_email && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <EmailOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {job.contact_email}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {job.company?.phone_number && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PhoneOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {job.company.phone_number}
                                            </Typography>
                                        </Stack>
                                    )}
                                </>
                            )}
                        </Stack>

                        <Button
                            component={RouterLink}
                            to={`/job/${id}/apply`}
                            variant="contained"
                            fullWidth
                            disableElevation
                            size="large"
                        >
                            Apply Now
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            size="large"
                            startIcon={<BookmarkBorderIcon />}
                            sx={{ mt: 1 }}
                        >
                            Save Job
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default JobDetailsPage;
